import xws from 'api/xws/';
import { SET_DEALER_DATA, SET_DEALER_LOGO_URL } from './actionTypes';
import { saveStats } from 'api/xws/saveStats/saveStatsWs';

// Actions
import { setLocalizationString } from 'store/reducers/localizationString/actions';
import { setStatusTracker } from 'store/reducers/statusTracker/actions';
import { setTokenId } from 'store/reducers/tokenId/actions';
import {
  getDealerSettings,
  setDealerSettings
} from 'store/reducers/dealerSettings/actions';
import { setDealerLocale } from 'store/reducers/dealerLocale/actions';
import {
  setPrivateRoute,
  setSelfCheckinEnabled,
  checkQuickApptEnabled,
  setGUID
} from 'store/reducers/userInteractions/actions';
import { ajaxCallbacks } from 'store/reducers/ajaxCall/actions';
import {
  gaClickTrackingEvent,
  gaCreateTrackers,
  gaCreateAsburyTrackers
} from 'store/reducers/ga/actions';
import {
  clickTrackingEvent,
  triggerShiftEvent
} from 'store/reducers/clickTracking/actions';
import {
  gtmStatusTrackerEvent,
  gtmSetDimension
} from 'store/reducers/gtm/actions';
import { goTo } from 'store/reducers/navigation/actions';
import {
  setAppoinment,
  getSelfCheckInDetails,
  setSelfCheckInStatus,
  setStatusTrackerURL,
  setIsSciNavigating,
  setSelfCheckInFlow,
  setIsDealerOpenNow,
  setInitialLoadingComplete
} from 'store/reducers/selfCheckIn/actions';
import { getAnonymousPromotion } from 'store/reducers/promotions/actions';
import { setRecallServices } from 'store/reducers/recall/actions';
import actions, {
  getCustomerPreferences
} from 'store/reducers/user/personInfo/actions';

// Selectors
import { getTokenId } from 'store/reducers/tokenId/selectors';
import { getWebkey, getQueryParam } from 'store/reducers/queryParams/selectors';
import { getDealerDataParams } from 'store/reducers/dealerData/selectors';
import { getAppName } from 'store/reducers/userInteractions/selectors';
import {
  selfCheckInError,
  getErrorType,
  isHasAlreadyBeenSCI
} from 'store/reducers/appointment/selfCheckIn/selectors';
import { getShiftServiceImpressionObj } from 'store/reducers/clickTracking/selectors';
import { getAttendanceStatus } from 'store/reducers/appointment/selectors';

// Utils
import { isTrue, isUndefined } from 'utils/varValidation';
import { setLanguageTag } from 'utils/DOM';

// Constants
import { getService } from 'constants/ajax';
import { GA_EVENTS } from 'constants/ga';
import { CLICKTRACKING_TRIGGERS } from 'constants/clickTracking';
import {
  GTM_STATUS_TRACKER_EVENTS,
  STATUS_TRACKER_ERROR_ROUTE
} from 'constants/statusTracker';
import {
  SCI_ERROR_ROUTE,
  SCI_CONFIRMATION_ROUTE,
  SELF_CHECK_IN_FLOW,
  CHECKED_IN,
  ATTENDANCE_STATUS,
  SCI_HOME_ROUTE
} from 'constants/selfCheckIn';
import { APP_NAMES } from 'constants/appName';

// API
import { statusTrackerWs } from 'api/xws/statusTracker/statusTrackerWs';

const { AT_DEALER } = ATTENDANCE_STATUS;
const { SELF_CHECK_IN, CONSUMER_8 } = APP_NAMES;
const {
  GENERIC_FLOW,
  NON_OPERATING_HOURS_CHECK_IN,
  NON_OPERATING_HOURS_ADDITIONAL_SERVICE_CHECK_IN
} = SELF_CHECK_IN_FLOW;

export const getDealerData = params => async (dispatch, getState) => {
  const dealerDataService = getService('data');
  const webkey = getWebkey(getState());
  dealerDataService.setPathVars({ webkey });
  dealerDataService.setCallBacks(dispatch(ajaxCallbacks()));
  params.countryCode = (params.countryCode || '').toUpperCase();
  params.locale = (params.locale || '').replace(/_.*/gi, function(pattern) {
    return pattern.toUpperCase();
  });

  const request = xws(dealerDataService).get(dealerDataService.getPath(), {
    params
  });
  return (await request).data;
};

export const changeLocale = localeCode => async (dispatch, getState) => {
  const params = getDealerDataParams(localeCode)(getState());
  const data = await dispatch(getDealerData(params));
  const {
    dealerData: { dealerLocalization, locale }
  } = data;
  await dispatch(setLocalizationString(dealerLocalization));
  await dispatch(setDealerLocale(locale));
  return data;
};

export const retrieveDealerData = (data, localeCode) => async (
  dispatch,
  getState
) => {
  let computedData = data;

  const params = getDealerDataParams(localeCode)(getState());

  // data might not be valid (it's injected as a meta tag),
  // in case it's not valid make a request to get it remotely
  if (!data || !data.success) {
    computedData = await dispatch(getDealerData(params));
  }

  // at this point it's ready
  const {
    dealerData: {
      dealerLocalization,
      dealerServerUrl,
      locale,
      clickTrackingDetails,
      dlrLoadConfig
    },
    tokenId
  } = computedData;
  await dispatch(
    initProcess(
      tokenId,
      dealerLocalization,
      dealerServerUrl,
      locale,
      clickTrackingDetails,
      dlrLoadConfig
    )
  );
};

const initProcess = (
  tokenId,
  dealerLocalization,
  dealerServerUrl,
  locale,
  clickTrackingDetails,
  dlrLoadConfig
) => async (dispatch, getState) => {
  if (tokenId) {
    dispatch(setLocalizationString(dealerLocalization));
    dispatch(setTokenId(tokenId));
    dispatch(setGUID());
    dispatch(
      setDealerData({
        dealerServerUrl,
        clickTrackingDetails,
        dlrLoadConfig
      })
    ); // TODO: put dealerLocale & tokenId inside dealerData
    const appName = getAppName(getState());
    // const isConsumer8 = !isTrue(getQueryParam('statusTracker')(getState()));
    setTimeout(() => {
      dispatch(setPrivateRoute(true));
    }, 0);
    setLanguageTag(locale.localeCode);
    if (CONSUMER_8 === appName) {
      dispatch(clickTrackingEvent(CLICKTRACKING_TRIGGERS.PREAMBLE));

      const token = getQueryParam('token')(getState());

      dispatch(callSaveStats());

      dispatch(setDealerLocale(locale));

      await dispatch(gaCreateTrackers());

      // @GA EVENT LOAD
      dispatch(gaClickTrackingEvent(GA_EVENTS.LOAD));
      dispatch(clickTrackingEvent(CLICKTRACKING_TRIGGERS.LOAD));
      // @Shift EVENT LOAD IMPRESSION
      dispatch(triggerShiftEvent(getShiftServiceImpressionObj(getState())));

      /* eslint-enable camelcase */
      await dispatch(getDealerSettings());
      !token && (await dispatch(getAnonymousPromotion()));
      await dispatch(checkQuickApptEnabled());
    } else if (SELF_CHECK_IN === appName) {
      dispatch(setDealerLocale(locale));
      const reservationId = getQueryParam('reservationId')(getState());

      try {
        const {
          appointment,
          dealerContact,
          dealerShowPricingWhenIdk,
          priceVisibility,
          locales,
          dealerName,
          logoUrl,
          makes,
          mileageUnit,
          selfCheckinEnabled,
          nonOperatingHoursSCIEnabled,
          selfCheckInStatus,
          statusTrackerURL,
          isDealerOpenNow,
          customerAddServiceSCI,
          tellusmoreSvcId
        } = await dispatch(getSelfCheckInDetails({ reservationId }));
        let selfCheckInFlow = '';

        dispatch(
          gtmSetDimension({
            reservationId,
            confirmationKey: appointment && appointment.confirmationKey
          })
        );

        if (isDealerOpenNow) {
          selfCheckInFlow = GENERIC_FLOW;
        } else if (nonOperatingHoursSCIEnabled) {
          if (customerAddServiceSCI && tellusmoreSvcId) {
            selfCheckInFlow = NON_OPERATING_HOURS_ADDITIONAL_SERVICE_CHECK_IN;
          } else {
            selfCheckInFlow = NON_OPERATING_HOURS_CHECK_IN;
          }
        } else {
          selfCheckInFlow = GENERIC_FLOW;
        }

        // @TODO: create one simple setter for configFlow
        dispatch(setSelfCheckInStatus(selfCheckInStatus));
        dispatch(setStatusTrackerURL(statusTrackerURL));
        dispatch(setSelfCheckinEnabled(selfCheckinEnabled));
        dispatch(setSelfCheckInFlow(selfCheckInFlow));
        dispatch(setIsDealerOpenNow(isDealerOpenNow));
        // @TODO: create a common method if is needed for more micro sites
        dispatch(
          setDealerSettings({
            policies: {
              dealerContact,
              dealerShowPricingWhenIdk,
              locales,
              name: dealerName,
              mileageUnit,
              tellusmoreSvcId
            },
            makes,
            priceVisibility
          })
        );
        dispatch(setDealerLogoURL(logoUrl));
        await dispatch(setAppoinment(appointment));
        if (
          appointment.autoRecallServices &&
          appointment.autoRecallServices.length > 0
        ) {
          dispatch(setRecallServices(appointment.autoRecallServices));
        }

        const isExtendedHoursCheckIn =
          getAttendanceStatus(getState()) === AT_DEALER &&
          selfCheckInStatus === CHECKED_IN;
        const error = selfCheckInError(getState()) || getErrorType(getState());
        if (error && !isExtendedHoursCheckIn) {
          dispatch(goTo(SCI_ERROR_ROUTE));
        } else if (isHasAlreadyBeenSCI(getState()) || isExtendedHoursCheckIn) {
          dispatch(goTo(SCI_CONFIRMATION_ROUTE));
        } else {
          dispatch(goTo(SCI_HOME_ROUTE));
        }
      } catch (e) {
        // Navigation to Self Check-in Error Page
        /* eslint-disable no-console */
        console.error(e);
        /* eslint-enable no-console */
        dispatch(goTo(SCI_ERROR_ROUTE));
      }
      dispatch(setIsSciNavigating(false));
      dispatch(setInitialLoadingComplete(true));
      // await dispatch(getAppointment({ reservationId }));
    } else {
      // @TODO: use switch statement instead if else
      // means that : STATUS_TRACKER === appName
      // Is StatusTracker
      dispatch(setDealerLocale(locale));
      const reservationId = getQueryParam('reservationId')(getState());
      const webKey = getWebkey(getState());
      dispatch(gaCreateAsburyTrackers());

      const params = {
        webKey,
        country: locale.localeCode.split('_')[1],
        language: locale.localeCode.split('_')[0],
        tokenId: getTokenId(getState())
      };
      try {
        const { data } = await statusTrackerWs(reservationId, dispatch, params);
        const {
          dealerContact,
          locales,
          dealerName,
          logoUrl,
          confirmationKey,
          statusTrackerEnabled,
          personId,
          ...statusTrackerData
        } = data;
        dispatch(setStatusTracker(statusTrackerData));
        dispatch(setDealerLogoURL(logoUrl));
        dispatch(getCustomerPreferences(personId));
        dispatch(actions.setPersonInfo({ personId }));
        dispatch(
          setDealerSettings({
            policies: {
              dealerContact,
              locales,
              name: dealerName
            }
          })
        );
        dispatch(
          gtmStatusTrackerEvent(
            reservationId,
            confirmationKey,
            GTM_STATUS_TRACKER_EVENTS.START
          )
        );
        if (!isTrue(statusTrackerEnabled)) {
          dispatch(
            gtmStatusTrackerEvent(
              reservationId,
              confirmationKey,
              GTM_STATUS_TRACKER_EVENTS.DISABLED
            )
          );
          dispatch(goTo(STATUS_TRACKER_ERROR_ROUTE));
        }
      } catch (e) {
        // Navigation to Error Status Tracker
        /* eslint-disable no-console */
        console.error(e);
        /* eslint-enable no-console */
        dispatch(goTo(STATUS_TRACKER_ERROR_ROUTE));
        dispatch(
          gtmStatusTrackerEvent(
            reservationId,
            null,
            GTM_STATUS_TRACKER_EVENTS.START
          )
        );
        dispatch(
          gtmStatusTrackerEvent(
            reservationId,
            null,
            GTM_STATUS_TRACKER_EVENTS.ERROR
          )
        );
      }
    }
  } else {
    /* eslint-disable no-console */
    console.log('error'); // TODO handling errors
    /* eslint-enable no-console */
  }
};

export const callSaveStats = (providerT, keywordT, campaignIdT) => (
  dispatch,
  getState
) => {
  let keyword;
  let provider;
  let campaignId;
  let webkey;
  const tokenId = getTokenId(getState());
  if (providerT || keywordT || campaignIdT) {
    provider = providerT;
    keyword = keywordT;
    campaignId = campaignIdT;
    webkey = getWebkey(getState());
  } else {
    keyword = getQueryParam('keyword')(getState());
    provider = getQueryParam('provider')(getState());
    campaignId = getQueryParam('campaign_id')(getState());
    webkey = getWebkey(getState());
  }
  /* eslint-disable camelcase */
  if (!isUndefined(keyword) && !isUndefined(provider)) {
    saveStats({
      params: {
        tokenId,
        event: 'CONSUMER_LANDING_KEYWORD',
        attribute_1: keyword,
        attribute_2: provider
      },
      webkey
    });
  }
  if (campaignId) {
    saveStats({
      params: {
        tokenId,
        event: 'CONSUMER_LANDING_KEYWORD',
        attribute_1: campaignId,
        attribute_2: webkey
      },
      webkey
    });
  }
};

export const setDealerData = dealerData => {
  return {
    type: SET_DEALER_DATA,
    payload: dealerData
  };
};

export const setDealerLogoURL = dealerLogoURL => {
  return {
    type: SET_DEALER_LOGO_URL,
    payload: dealerLogoURL
  };
};



// WEBPACK FOOTER //
// ./src/store/reducers/dealerData/actions.js